import { APITypes } from "@cur8/api-client";
import { Patient } from "@cur8/rich-entity";
import { Sticky, ViewStack } from "@pomle/react-viewstack";
import { LegalDocument, hasBeenInformedAbout } from "lib/legal";
import { findNotParticipatedStudies } from "lib/studies/findNotParticipatedStudies";
import { ReactElement, useEffect, useMemo, useState } from "react";
import { useAppInsights } from "render/context/AppInsightsContext";
import { ReactComponent as ArrowIcon } from "render/ui/icons/arrow.svg";
import { FullScreenPageLayout } from "render/ui/layout/FullScreenPageLayout";
import { LogoHeader } from "render/ui/layout/LogoHeader/LogoHeader";
import { IconButton } from "render/ui/trigger/IconButton/IconButton";
import { useConsentsMap } from "render/views/ProfileView/components/DataAndPrivacySection/hooks/useConsentsMap";
import { Transition } from "../__shared/Transition";
import { CheckInCompleteStep } from "./components/CheckInCompleteStep";
import { ConsentAutoSummaryDocument } from "./components/ConsentAutoSummaryDocument";
import { ConsentAutoSummaryExplanation } from "./components/ConsentAutoSummaryExplanation";
import { ConsentRequiredStudiesDocument } from "./components/ConsentRequiredStudiesDocument";
import { ConsentRequiredStudiesExplanation } from "./components/ConsentRequiredStudiesExplanation";
import styles from "./styles.module.sass";

interface CheckInFlowViewProps {
  patient: Patient;
  consents: APITypes.ConsentSummary[];
  updateConsents: () => Promise<any>;
}

enum Page {
  First = "first",
  Second = "second",
}

enum Step {
  Complete,
  StudyConsentExplanation,
  StudyConsentDocument,
  AutomaticSummaryConsentExplanation,
  AutomaticSummaryConsentDocument,
}

interface View {
  key: Step;
  component: () => ReactElement;
}

export function CheckInFlowView({
  patient,
  consents,
  updateConsents,
}: CheckInFlowViewProps) {
  const { trackEvent } = useAppInsights();
  const [studiesPage, setStudiesPage] = useState(Page.First);
  const [autoSummariesPage, setAutoSummariesPage] = useState(Page.First);

  const { studies } = useConsentsMap();
  const notParticipatedStudies = useMemo(() => {
    return findNotParticipatedStudies(consents, studies);
  }, [consents, studies]);

  const hasAgreedToAllStudies = useMemo(() => {
    return notParticipatedStudies.length === 0;
  }, [notParticipatedStudies]);

  const autoSummariesAcknowledged = useMemo(() => {
    return hasBeenInformedAbout(LegalDocument.AutomaticSummary, consents);
  }, [consents]);

  const currentStep = useMemo(() => {
    if (!hasAgreedToAllStudies && studiesPage === Page.First) {
      return Step.StudyConsentExplanation;
    }
    if (!hasAgreedToAllStudies && studiesPage === Page.Second) {
      return Step.StudyConsentDocument;
    }
    if (!autoSummariesAcknowledged && autoSummariesPage === Page.First) {
      return Step.AutomaticSummaryConsentExplanation;
    }
    if (!autoSummariesAcknowledged && autoSummariesPage === Page.Second) {
      return Step.AutomaticSummaryConsentDocument;
    }

    return Step.Complete;
  }, [
    hasAgreedToAllStudies,
    studiesPage,
    autoSummariesAcknowledged,
    autoSummariesPage,
  ]);

  const steps = useMemo<View[]>(
    () => [
      {
        key: Step.StudyConsentExplanation,
        component: () => (
          <ConsentRequiredStudiesExplanation
            onDone={() => setStudiesPage(Page.Second)}
          />
        ),
      },
      {
        key: Step.StudyConsentDocument,
        component: () => (
          <ConsentRequiredStudiesDocument
            notParticipatedStudies={notParticipatedStudies}
            onDone={updateConsents}
            onRefreshConsents={updateConsents}
          />
        ),
      },
      {
        key: Step.AutomaticSummaryConsentExplanation,
        component: () => (
          <ConsentAutoSummaryExplanation
            onDone={() => setAutoSummariesPage(Page.Second)}
          />
        ),
      },
      {
        key: Step.AutomaticSummaryConsentDocument,
        component: () => (
          <ConsentAutoSummaryDocument
            patient={patient}
            onDone={updateConsents}
          />
        ),
      },
      {
        key: Step.Complete,
        component: () => <CheckInCompleteStep patient={patient} />,
      },
    ],
    [notParticipatedStudies, patient, updateConsents]
  );
  const currentStepIndex = useMemo(() => {
    return steps.findIndex(({ key }) => key === currentStep);
  }, [steps, currentStep]);

  useEffect(() => {
    trackEvent({
      name: "concierge-check-in-success",
      properties: { step: currentStep },
    });
  }, [currentStep, trackEvent]);

  return (
    <FullScreenPageLayout disableBackground>
      <div className={styles.CheckInFlowView}>
        <ViewStack>
          {steps.map(({ key, component }, index) => {
            const match = key === currentStep;
            const offset = index - currentStepIndex;
            return (
              <Transition offset={offset} key={key}>
                <Sticky>{match && component()}</Sticky>
              </Transition>
            );
          })}
        </ViewStack>
        <div className={styles.header}>
          <LogoHeader
            leftElement={
              <>
                {currentStep === Step.StudyConsentDocument && (
                  <IconButton
                    onClick={() => setStudiesPage(Page.First)}
                    icon={<ArrowIcon />}
                  />
                )}
                {currentStep === Step.AutomaticSummaryConsentDocument && (
                  <IconButton
                    onClick={() => setAutoSummariesPage(Page.First)}
                    icon={<ArrowIcon />}
                  />
                )}
              </>
            }
          />
        </div>
      </div>
    </FullScreenPageLayout>
  );
}
